import { useState, useEffect } from "react";
import axios from "../utils/axiosConfig";
import Icon from "../components/Icon";

// Reusable Dashboard Card Component
function DashboardCard({ title, value, icon, color, onClick }) {
  return (
    <div style={{
      ...styles.card,
      borderLeft: `4px solid ${color}`,
      ...(onClick ? { cursor: 'pointer' } : {})
    }} onClick={onClick}>
      <div style={styles.cardIcon}>
        <Icon name={icon} size={32} color={color} />
      </div>
      <div style={styles.cardContent}>
        <h3 style={styles.cardValue}>{value}</h3>
        <p style={styles.cardTitle}>{title}</p>
      </div>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState({
    totalTcds: 0,
    pendingTcds: 0,
    totalMtc: 0,
    recentRecords: 0,
    userRole: '',
    unitName: null
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get user info from localStorage
  const getUserFromStorage = () => {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  };

  const currentUser = getUserFromStorage();
  const userRole = (localStorage.getItem("role") || "").toLowerCase();

  const getDashboardTitle = () => {
    if (userRole === "admin") return "Admin Dashboard";
    if (userRole === "quality") return "Quality Dashboard";
    return "Sales Dashboard";
  };

  const getWelcomeMessage = () => {
    const name = currentUser?.name || currentUser?.username || "User";
    const unit = stats.unitName ? ` - ${stats.unitName}` : '';
    return `Welcome, ${name}${unit}`;
  };

  const getRecentActivityLabel = () => {
    if (userRole === "quality") return "Recent MTC Generated";
    return "Recent TCDS Created";
  };

  const getRoleBadge = () => {
    const badges = {
      admin: { bg: "#7c3aed", text: "Admin" },
      quality: { bg: "#10b981", text: "Quality" },
      sales: { bg: "#3b82f6", text: "Sales" }
    };
    const badge = badges[userRole] || badges.sales;
    return (
      <span style={{
        ...styles.roleBadge,
        backgroundColor: badge.bg
      }}>
        {badge.text}
      </span>
    );
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/dashboard/stats");
      const data = response.data;

      setStats({
        totalTcds: data.totalTcds || 0,
        pendingTcds: data.pendingTcds || 0,
        totalMtc: data.totalMtc || 0,
        recentRecords: data.recentActivity?.length || 0,
        userRole: data.userRole || userRole,
        unitName: data.unitName || null
      });

      setRecentActivity(data.recentActivity || []);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.loading}>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{getDashboardTitle()}</h1>
          <p style={styles.welcome}>{getWelcomeMessage()}</p>
        </div>
        {getRoleBadge()}
      </div>

      {/* Statistics Cards */}
      <div style={styles.cardsGrid}>
        <DashboardCard
          title="Total TCDS"
          value={stats.totalTcds}
          icon="document"
          color="#3b82f6"
        />
        <DashboardCard
          title="Pending TCDS (Awaiting MTC)"
          value={stats.pendingTcds}
          icon="clock"
          color="#f59e0b"
        />
        <DashboardCard
          title="Total MTC Generated"
          value={stats.totalMtc}
          icon="check"
          color="#10b981"
        />
        <DashboardCard
          title={getRecentActivityLabel()}
          value={stats.recentRecords}
          icon="chart"
          color="#8b5cf6"
        />
      </div>

      {/* Role-specific message */}
      {userRole === "admin" && (
        <div style={styles.adminNote}>
          <strong>Note:</strong> Admin has access to User Management and Master Configuration only. TCDS is managed by Sales users and MTC is managed by Quality users.
        </div>
      )}

      {userRole === "sales" && (
        <div style={styles.salesNote}>
          <strong>Quick Actions:</strong> Create new TCDS from the TCDS Management section. Pending TCDS are those awaiting MTC generation by Quality team.
        </div>
      )}

      {userRole === "quality" && (
        <div style={styles.qualityNote}>
          <strong>Quick Actions:</strong> Generate MTC from completed TCDS. View pending TCDS that need MTC generation.
        </div>
      )}

      {/* Recent Activity Section */}
      <div style={styles.activitySection}>
        <h2 style={styles.sectionTitle}>
          {userRole === "quality" ? "Last 5 MTC Generated" : "Last 5 TCDS Created"}
        </h2>

        {recentActivity.length > 0 ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.tableHeader}>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Reference No.</th>
                  <th style={styles.th}>Created By</th>
                  {userRole === "admin" && <th style={styles.th}>Unit</th>}
                  <th style={styles.th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentActivity.slice(0, 5).map((activity, idx) => (
                  <tr key={activity.id || idx} style={styles.tableRow}>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.typeBadge,
                        backgroundColor: activity.type === 'TCDS' ? '#dbeafe' : '#d1fae5',
                        color: activity.type === 'TCDS' ? '#1e40af' : '#065f46'
                      }}>
                        {activity.type}
                      </span>
                    </td>
                    <td style={styles.td}>{activity.reference || '-'}</td>
                    <td style={styles.td}>{activity.created_by || '-'}</td>
                    {userRole === "admin" && <td style={styles.td}>{activity.unit_id || "—"}</td>}
                    <td style={styles.td}>
                      {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={styles.noData}>No recent activity</p>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "0",
    maxWidth: "1400px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "600",
    color: "#333",
    margin: "0 0 8px 0",
  },
  welcome: {
    fontSize: "14px",
    color: "#666",
    margin: 0,
  },
  roleBadge: {
    padding: "6px 16px",
    borderRadius: "20px",
    color: "white",
    fontSize: "13px",
    fontWeight: "600",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    fontSize: "16px",
    color: "#666",
  },
  cardsGrid: {
    display: "flex",
    gap: "20px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  card: {
    flex: "1",
    minWidth: "220px",
    backgroundColor: "white",
    padding: "24px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  cardIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardContent: {
    flex: "1",
  },
  cardValue: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#333",
    margin: "0 0 8px 0",
  },
  cardTitle: {
    fontSize: "13px",
    color: "#666",
    margin: "0",
    fontWeight: "500",
  },
  adminNote: {
    backgroundColor: "#fffbe6",
    border: "1px solid #ffe58f",
    borderRadius: "6px",
    padding: "10px 16px",
    fontSize: "13px",
    color: "#7d5c00",
    marginBottom: "20px",
  },
  salesNote: {
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "6px",
    padding: "10px 16px",
    fontSize: "13px",
    color: "#1e40af",
    marginBottom: "20px",
  },
  qualityNote: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #a7f3d0",
    borderRadius: "6px",
    padding: "10px 16px",
    fontSize: "13px",
    color: "#065f46",
    marginBottom: "20px",
  },
  activitySection: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "20px",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    borderBottom: "2px solid #ddd",
  },
  tableRow: {
    borderBottom: "1px solid #ddd",
    transition: "background-color 0.2s",
  },
  td: {
    padding: "12px 16px",
    fontSize: "14px",
    color: "#666",
  },
  typeBadge: {
    padding: "4px 10px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
  },
  noData: {
    textAlign: "center",
    padding: "40px",
    color: "#999",
    fontSize: "14px",
  },
};

export default Dashboard;
